// ─── src/components/reader/LuxuryFlipBook.jsx ───────────────────────────────
// LuxuryFlipBook, split-path flip engine.
//
// Desktop (isPortrait=false, ≥640px):
//   StPageFlip (page-flip 2.x), paper-curl 3D animation, double spread.
//
// Mobile (isPortrait=true, <640px):
//   CSS scroll-snap slider, all pages in a horizontal flex row with
//   scroll-snap-type:x mandatory. Native browser scroll + momentum.
//   Zero JS animation, zero black-screen risk. Swipe natively, tap
//   zones + NavButtons call scrollTo({ behavior:'smooth' }).
//
// Layout:
//   • Tablet/desktop (≥640px) → double spread + paper-curl flip
//   • Mobile (<640px)         → single page portrait + CSS-3D swipe flip
//
// Imperative API (via ref, same surface for both paths):
//   • flipNext()
//   • flipPrev()
//   • turnTo(pageNumber)  , 1-indexed
//   • getCurrentPage()    , 1-indexed

import { useEffect, useImperativeHandle, useRef, useState, useCallback, forwardRef } from "react";
import { imgUrl } from "../../utils/imgUrl";
import { PageFlip } from "page-flip";

// ─── Reduced-motion detection ───────────────────────────────────────────────
function getReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE PATH, vertical image stack.
//
// Pages are stacked top-to-bottom in a single scrollable column.
// The browser owns all scrolling, momentum, rubber-band, everything.
// Zero JS animation, zero black-screen risk, works on every device.
//
// IntersectionObserver tracks which page is most visible to keep the
// page counter accurate. Imperative API (flipNext/flipPrev/turnTo)
// calls scrollIntoView on the target page's img element.
// ─────────────────────────────────────────────────────────────────────────────
const MobileVerticalStack = forwardRef(function MobileVerticalStack(
  { pages = [], startPage = 1, onFlip, onReady, debug = false },
  ref
) {
  const [current, setCurrent] = useState(Math.max(0, startPage - 1));
  const imgRefs               = useRef([]);   // one ref per page img
  const lastFiredRef          = useRef(Math.max(0, startPage - 1));

  useEffect(() => { onReady?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Jump to startPage on mount (no animation) ───────────────────────────
  useEffect(() => {
    if (pages.length === 0) return;
    const target = Math.max(0, startPage - 1);
    const el = imgRefs.current[target];
    if (el) el.scrollIntoView({ block: "start", behavior: "instant" });
  }, [pages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track visible page via IntersectionObserver ──────────────────────────
  useEffect(() => {
    if (pages.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Find the entry with the highest intersection ratio
        let best = null;
        entries.forEach(e => {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        });
        if (!best || !best.isIntersecting) return;
        const idx = Number(best.target.dataset.pageIdx);
        if (!Number.isNaN(idx) && idx !== lastFiredRef.current) {
          lastFiredRef.current = idx;
          setCurrent(idx);
          onFlip?.(idx + 1);
        }
      },
      { threshold: [0.3, 0.5, 0.7] }
    );
    imgRefs.current.forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [pages.length, onFlip]);

  // ── Imperative API ───────────────────────────────────────────────────────
  const scrollTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(pages.length - 1, idx));
    const el = imgRefs.current[clamped];
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [pages.length]);

  useImperativeHandle(ref, () => ({
    flipNext:       () => scrollTo(current + 1),
    flipPrev:       () => scrollTo(current - 1),
    turnTo:         (n) => scrollTo(Math.max(0, (n || 1) - 1)),
    getCurrentPage: () => current + 1,
  }), [current, scrollTo]);

  if (!Array.isArray(pages) || pages.length === 0) {
    return (
      <div style={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "rgba(245,240,232,0.4)", fontFamily: "'Jost', sans-serif",
        fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase",
      }}>
        ◈ Loading
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>

      {/* ── Page stack, plain vertical block flow ── */}
      <div style={{ width: "100%", height: "100%", overflowY: "auto", overflowX: "hidden" }}>
        {pages.map((p, i) => (
          <div
            key={p.id ?? i}
            style={{ width: "100%", lineHeight: 0 }}
          >
            {p.image_url ? (
              <img
                ref={el => { imgRefs.current[i] = el; }}
                data-page-idx={i}
                src={imgUrl(p.image_url, { width: 1600, resize: 'contain' })}
                alt={`Page ${i + 1}`}
                loading={i <= 1 ? "eager" : "lazy"}
                decoding="async"
                draggable={false}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  userSelect: "none",
                }}
              />
            ) : (
              <div
                ref={el => { imgRefs.current[i] = el; }}
                data-page-idx={i}
                style={{
                  width: "100%", paddingBottom: "141.4%",
                  background: "#1a1714", position: "relative",
                }}
              >
                <span style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%,-50%)",
                  color: "rgba(245,240,232,0.25)", fontFamily: "'Jost', sans-serif", fontSize: 12,
                }}>
                  Page {i + 1}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Page counter pill ── */}
      <div aria-hidden style={{
        position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.6)", color: "rgba(255,255,255,0.7)",
        fontFamily: "'Jost', sans-serif", fontSize: 11, letterSpacing: "0.15em",
        padding: "4px 12px", borderRadius: 20,
        pointerEvents: "none", whiteSpace: "nowrap", zIndex: 10,
      }}>
        {current + 1} / {pages.length}
      </div>

    </div>
  );
});


// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP PATH, StPageFlip (page-flip 2.x)
// ─────────────────────────────────────────────────────────────────────────────
const DesktopPageFlip = forwardRef(function DesktopPageFlip(
  {
    pages = [],
    startPage = 1,
    pageBg = "#0F0D0A",
    pageAspect = 1.414,
    onFlip,
    onPageView,
    onReady,
    style,
    className,
    debug = false,
  },
  ref
) {
  const containerRef = useRef(null);
  const pageFlipRef  = useRef(null);
  const [loading, setLoading]   = useState(true);
  const [currentPage, setCurrentPage] = useState(startPage);
  const [error, setError]       = useState(null);

  useImperativeHandle(ref, () => ({
    flipNext: () => {
      try { pageFlipRef.current?.flipNext(); } catch { /* engine not ready */ }
    },
    flipPrev: () => {
      try { pageFlipRef.current?.flipPrev(); } catch { /* engine not ready */ }
    },
    turnTo: (pageNumber) => {
      const idx = Math.max(0, (pageNumber || 1) - 1);
      try { pageFlipRef.current?.turnToPage(idx); } catch { /* engine not ready */ }
    },
    getCurrentPage: () => currentPage,
  }), [currentPage]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const container = containerRef.current;
    if (!container) return;
    if (!Array.isArray(pages) || pages.length === 0) return;

    const reducedMotion = getReducedMotion();
    let pf = null;
    let cancelled = false;
    let rafId = null;

    const init = () => {
      if (cancelled) return;
      try {
        // Canonical single-page dimensions. PageFlip interprets `width` as
        // PER-PAGE width, NOT spread width. Passing the container's rect.width
        // (which is the full SPREAD width) caused the engine to treat each
        // page as ~1900px wide, splitting the spread across a 3800px canvas
        // with a huge gap in the middle. size:"stretch" + autoSize:true
        // handles the actual fit-to-container resize after init.
        const initW = 550;
        const initH = Math.floor(initW * pageAspect);

        pf = new PageFlip(container, {
          width:  initW,
          height: initH,
          size: "stretch",
          minWidth:  280,
          maxWidth:  1200,
          minHeight: Math.floor(280 * pageAspect),
          maxHeight: Math.floor(1200 * pageAspect),
          showCover:        true,
          usePortrait:      false,   // desktop path is always spread
          autoSize:         true,
          maxShadowOpacity: 0.55,
          drawShadow:       !reducedMotion,
          flippingTime:     reducedMotion ? 0 : 700,
          mobileScrollSupport: false,
          useMouseEvents:      true,
          swipeDistance:       30,
          clickEventForward:   false,
          startPage: Math.max(0, (startPage || 1) - 1),
        });

        pf.on("flip", (e) => {
          if (cancelled) return;
          const newPage = (e.data ?? 0) + 1;
          setCurrentPage(newPage);
          onFlip?.(newPage);
          onPageView?.(newPage);
        });
        // Re-sync on settle. StPageFlip's 'flip' event under-reports when the
        // book CLOSES back onto the cover (reverse-to-cover) and when it settles
        // on the back cover, the page stays stale, so the lone cover renders
        // off-centre with a white facing half. When the engine settles into the
        // 'read' state, read its authoritative current index and re-sync so the
        // cover-centring logic in the parent always sees the true page.
        pf.on("changeState", (e) => {
          if (cancelled) return;
          if (e.data !== "read") return;
          try {
            const idx = pf.getCurrentPageIndex();
            if (typeof idx === "number") {
              const np = idx + 1;
              setCurrentPage(np);
              onFlip?.(np);
            }
          } catch { /* engine not ready */ }
        });
        pf.on("init", () => {
          if (cancelled) return;
          setLoading(false);
          onReady?.();
          onPageView?.(startPage || 1);
        });

        const pageEls = container.querySelectorAll("[data-lwd-page]");
        if (pageEls.length > 0) {
          pf.loadFromHTML(pageEls);
        } else {
          console.warn(
            "[LuxuryFlipBook] No [data-lwd-page] children in DOM, " +
            "falling back to loadFromImages"
          );
          const urls = pages.map(p => imgUrl(p.image_url, { width: 1600, resize: 'contain' })).filter(Boolean);
          if (urls.length === 0) throw new Error("No page images available");
          pf.loadFromImages(urls);
        }

        pageFlipRef.current = pf;

        // Belt-and-braces: StPageFlip's 'init' event is unreliable on
        // some browsers (notably mobile WebKit/iOS Safari and certain
        // resize/HMR sequences). When init never fires, `loading` stays
        // true and the dark loading overlay covers the whole flipbook
        // permanently, visible to the user as a black screen with no
        // diagnostic. Force-clear loading after a short grace period.
        // If init does fire normally, this is a no-op (state already false).
        const initFallback = setTimeout(() => {
          if (cancelled) return;
          setLoading(false);
          onReady?.();
        }, 800);
        // Track on the engine so cleanup can clear it
        pf._initFallbackTimer = initFallback;

        // Page-sync backstop. StPageFlip's 'flip'/'changeState' events can
        // under-report after a reverse flip (closing onto the cover / settling
        // on the back cover), leaving the parent's currentPage stale, which
        // breaks the cover-centring. Poll the engine's authoritative index a
        // few times a second and re-sync only when it actually disagrees, so
        // the centring logic always sees the true page no matter what fires.
        let lastSynced = Math.max(0, (startPage || 1) - 1);
        const syncTimer = setInterval(() => {
          if (cancelled) return;
          try {
            const idx = pf.getCurrentPageIndex();
            if (typeof idx === "number" && idx !== lastSynced) {
              lastSynced = idx;
              const np = idx + 1;
              setCurrentPage(np);
              onFlip?.(np);
            }
          } catch { /* engine not ready */ }
        }, 250);
        pf._syncTimer = syncTimer;
      } catch (e) {
        console.error("[LuxuryFlipBook] PageFlip init failed:", e);
        if (!cancelled) {
          setError(e?.message || "Failed to initialise flipbook");
          setLoading(false);
        }
      }
    };

    // Double-rAF: first frame commits React children; second waits for
    // CSS layout to settle so getBoundingClientRect() returns real dims.
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(init);
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      try { clearTimeout(pf?._initFallbackTimer); } catch { /* */ }
      try { clearInterval(pf?._syncTimer); } catch { /* */ }
      try { pf?.destroy?.(); } catch { /* engine half-mounted */ }
      pageFlipRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages?.length]);

  // Static fallback removed. The original safety net was added for
  // iOS Safari init failures, but iOS Safari now routes to the separate
  // MobileCSSFlipBook path (<640px → portrait). The fallback's full-
  // container `objectFit: contain` rendered the cover image centred,
  // while the engine canvas in single-cover spread mode only fills the
  // right half, so the centred fallback bled out the left as a ghost
  // duplicate offset from the actual page.
  return (
    <div
      className={className}
      // Transparent root: lets the empty half of a lone cover / back-cover
      // spread show the reader backdrop instead of a page-coloured rectangle.
      // Interior pages are opaque images, so two-page spreads are unaffected.
      style={{ position: "relative", width: "100%", height: "100%", background: "transparent", ...style }}
    >
      <div
        ref={containerRef}
        style={{ position: "relative", zIndex: 1, width: "100%", height: "100%", background: "transparent" }}
      >
        {pages.map((p, i) => (
          <div
            key={p.id ?? p.page_number ?? i}
            data-lwd-page
            data-page-number={p.page_number ?? (i + 1)}
            style={{
              width: "100%", height: "100%",
              background: pageBg, overflow: "hidden",
              display: "flex", alignItems: "center", justifyContent: "center",
              willChange: "transform",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
            }}
          >
            {p.image_url ? (
              <img
                src={imgUrl(p.image_url, { width: 1600, resize: 'contain' })}
                alt={`Page ${p.page_number ?? i + 1}`}
                draggable={false}
                style={{
                  width: "100%", height: "100%",
                  objectFit: "contain",
                  userSelect: "none", pointerEvents: "none", display: "block",
                }}
              />
            ) : (
              <span style={{ color: "rgba(245,240,232,0.3)", fontSize: 14 }}>
                Page {p.page_number ?? i + 1}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Loading overlay removed entirely. Was previously a dark overlay
          that covered everything until StPageFlip's 'init' event fired,
          but on mobile WebKit init often never fires, so the overlay
          stayed forever and the user saw a permanent black screen. The
          static fallback above ensures the page is always visible during
          init, init failure, and engine destruction. */}

      {error && (
        <div role="alert" style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 12, padding: 24, background: pageBg,
          color: "rgba(245,240,232,0.6)",
          fontFamily: "'Jost', sans-serif", fontSize: 12, textAlign: "center",
        }}>
          <span style={{ fontSize: 24, opacity: 0.5 }}>◈</span>
          <span>{error}</span>
        </div>
      )}

      {debug && (
        <div aria-hidden style={{
          position: "fixed", bottom: 12, left: 12, zIndex: 99999,
          background: "rgba(225,6,0,0.9)", color: "#fff",
          padding: "8px 12px", fontFamily: "monospace",
          fontSize: 11, fontWeight: 600, lineHeight: 1.4,
          borderRadius: 4, pointerEvents: "none",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>
          ▣ DesktopPageFlip<br />
          page: {currentPage}/{pages.length}<br />
          portrait: false<br />
          ready: {String(!loading)}
        </div>
      )}
    </div>
  );
});


// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC EXPORT, routes to mobile or desktop path via isPortrait
// ─────────────────────────────────────────────────────────────────────────────
const LuxuryFlipBook = forwardRef(function LuxuryFlipBook(props, ref) {
  const { isPortrait = false } = props;

  if (isPortrait) {
    return <MobileSnapSlider {...props} ref={ref} />;
  }
  return <DesktopPageFlip {...props} ref={ref} />;
});

export default LuxuryFlipBook;
